import { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import type { Artifact } from '@/types';
import { cn } from '@/lib/utils';

interface DataTableProps {
  artifact: Artifact;
}

interface TableData {
  headers: string[];
  rows: string[][];
}

function parseTableContent(content: string): TableData {
  // Try to parse as JSON first
  try {
    const data = JSON.parse(content);
    if (Array.isArray(data) && data.length > 0) {
      const headers = Object.keys(data[0] as object);
      const rows = data.map((row) =>
        headers.map((h) => String((row as Record<string, unknown>)[h] ?? ''))
      );
      return { headers, rows };
    }
  } catch {
    // Not JSON, try to parse as markdown table
  }

  // Parse markdown table format
  const lines = content.trim().split('\n').filter(Boolean);
  if (lines.length < 2) {
    return { headers: ['Data'], rows: [[content]] };
  }

  // First line is headers
  const headers = lines[0]!
    .split('|')
    .map((h) => h.trim())
    .filter(Boolean);

  // Skip separator line (second line)
  const rows = lines.slice(2).map((line) =>
    line
      .split('|')
      .map((cell) => cell.trim())
      .filter((_, i, arr) => i > 0 && i < arr.length - 1 || arr.length === 1)
  );

  return { headers, rows };
}

export function DataTable({ artifact }: DataTableProps) {
  const [sortColumn, setSortColumn] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = useState('');

  const { headers, rows } = useMemo(
    () => parseTableContent(artifact.content),
    [artifact.content]
  );

  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;

    const query = searchQuery.toLowerCase();
    return rows.filter((row) =>
      row.some((cell) => cell.toLowerCase().includes(query))
    );
  }, [rows, searchQuery]);

  const sortedRows = useMemo(() => {
    if (sortColumn === null) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      const aVal = a[sortColumn] ?? '';
      const bVal = b[sortColumn] ?? '';

      // Try numeric comparison
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // String comparison
      return sortDirection === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });
  }, [filteredRows, sortColumn, sortDirection]);

  const handleSort = (columnIndex: number) => {
    if (sortColumn === columnIndex) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnIndex);
      setSortDirection('asc');
    }
  };

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-700 px-4 py-2">
        <span className="text-sm font-medium text-gray-100">
          {artifact.title ?? 'Data Table'}
        </span>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          {sortedRows.length} rows
        </div>
      </div>

      {/* Search */}
      {rows.length > 5 && (
        <div className="border-b border-gray-700 px-4 py-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="w-full rounded-md border border-gray-600 bg-gray-900 py-1.5 pl-9 pr-3 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-100">
          <thead>
            <tr className="border-b border-gray-700 bg-gray-900">
              {headers.map((header, i) => (
                <th
                  key={i}
                  onClick={() => handleSort(i)}
                  className={cn(
                    'cursor-pointer px-4 py-2 text-left font-medium text-gray-300 hover:bg-gray-800',
                    sortColumn === i && 'text-blue-400'
                  )}
                >
                  <div className="flex items-center gap-1">
                    {header}
                    {sortColumn === i && (
                      sortDirection === 'asc' ? (
                        <ChevronUp className="h-3 w-3" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      )
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={headers.length}
                  className="px-4 py-8 text-center text-gray-400"
                >
                  {searchQuery ? 'No matching rows' : 'No data'}
                </td>
              </tr>
            ) : (
              sortedRows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="border-b border-gray-700 last:border-0 hover:bg-gray-700/50"
                >
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="px-4 py-2 text-gray-200">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
